import { supabase, isSupabaseConfigured } from './supabase';

function guard() {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase is not configured. Add your 2026 project URL and anon key to .env.');
  }
}

function table(name, orderBy) {
  return {
    async getAll() {
      guard();
      let q = supabase.from(name).select('*');
      (orderBy || []).forEach(([col, opts]) => { q = q.order(col, opts); });
      const { data, error } = await q;
      if (error) { console.error(`Error fetching ${name}:`, error); return []; }
      return data || [];
    },
    async upsert(row) {
      guard();
      const { data, error } = await supabase.from(name).upsert([row]).select().single();
      if (error) { console.error(`Error upserting ${name}:`, error); throw error; }
      return data;
    },
    async upsertMany(rows) {
      guard();
      if (!rows?.length) return [];
      const out = [];
      // Chunked so large schedule generations don't hit payload limits.
      for (let i = 0; i < rows.length; i += 200) {
        const chunk = rows.slice(i, i + 200);
        const { data, error } = await supabase.from(name).upsert(chunk).select();
        if (error) { console.error(`Error upserting ${name} batch:`, error); throw error; }
        out.push(...(data || []));
      }
      return out;
    },
    async update(id, updates) {
      guard();
      const { data, error } = await supabase.from(name).update(updates).eq('id', id).select().single();
      if (error) { console.error(`Error updating ${name}:`, error); throw error; }
      return data;
    },
    async remove(id) {
      guard();
      const { error } = await supabase.from(name).delete().eq('id', id);
      if (error) { console.error(`Error deleting ${name}:`, error); throw error; }
    },
    async removeWhere(column, value) {
      guard();
      const { error } = await supabase.from(name).delete().eq(column, value);
      if (error) { console.error(`Error deleting ${name}:`, error); throw error; }
    },
    async removeAll() {
      guard();
      const { error } = await supabase.from(name).delete().neq('id', '__none__');
      if (error) { console.error(`Error clearing ${name}:`, error); throw error; }
    },
  };
}

export const db = {
  golfers:      table('golfers', [['division', {}], ['seed', {}]]),
  courses:      table('courses', [['sort_order', {}]]),
  teeSets:      table('tee_sets', [['course_id', {}], ['sort_order', {}]]),
  seasonDates:  table('season_dates', [['seq', {}]]),
  assignments:  table('date_assignments', [['season_date_id', {}]]),
  pairings:     table('division_pairings', [['season_date_id', {}]]),
  matches:      table('matches', [['match_date', { ascending: true }], ['tee_time', { ascending: true }]]),
  tiebreaks:    table('tiebreak_resolutions', [['resolved_at', { ascending: false }]]),

  settings: {
    async getAll() {
      guard();
      const { data, error } = await supabase.from('app_settings').select('*');
      if (error) { console.error('Error fetching settings:', error); return {}; }
      return Object.fromEntries((data || []).map(r => [r.key, r.value]));
    },
    async set(key, value) {
      guard();
      const { error } = await supabase.from('app_settings').upsert([{ key, value }]);
      if (error) { console.error('Error saving setting:', error); throw error; }
    },
  },

  // Delete every regular-season match (used before regenerating a schedule)
  async clearRegularSeasonMatches() {
    guard();
    const { error } = await supabase.from('matches').delete().eq('phase', 'regular');
    if (error) throw error;
  },

  async clearPlayoffMatches() {
    guard();
    const { error } = await supabase.from('matches').delete().eq('phase', 'playoff');
    if (error) throw error;
  },
};
